import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Container, Link, Paper, Stack, Typography } from '@mui/material';
import { TAGLINE } from '../../components/brand/Logo';
import { List, P, SUPPORT_EMAIL } from '../legal/LegalPage';

/** Where the extension is installed from. Empty until it is listed; the guide then says so in words only. */
export const EXTENSION_STORE_URL = '';

/** Something the product shows on screen, quoted as it is written there. */
const Ui: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="strong" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const To: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link component={RouterLink} to={to}>
    {children}
  </Link>
);

interface Step {
  title: string;
  body: React.ReactNode;
}

const STEPS: Step[] = [
  {
    title: 'Sign in',
    body: (
      <>
        Go to <To to="/login">the sign-in page</To> and choose <Ui>Continue with Google</Ui>. The same button creates your
        account the first time. There is no password to remember.
      </>
    ),
  },
  {
    title: 'Add the extension to Chrome',
    body: (
      <>
        {EXTENSION_STORE_URL ? (
          <>
            Install the <Link href={EXTENSION_STORE_URL} target="_blank" rel="noopener noreferrer">JobFlo extension</Link>.
          </>
        ) : (
          <>Install the JobFlo extension in Chrome.</>
        )}{' '}
        It signs in with your account on its own. Your <To to="/dashboard">Dashboard</To> shows{' '}
        <Ui>Extension connected</Ui> when it has; if it shows <Ui>Connect extension</Ui> instead, press it.
      </>
    ),
  },
  {
    title: 'Fill in your profile and upload a resume',
    body: (
      <>
        On <To to="/profile">Profile</To>, add your contact details, address, links, education and experience. On{' '}
        <To to="/resumes">Resumes</To>, press <Ui>Upload resume</Ui>. The one marked <Ui>Default</Ui> is the one attached
        to applications. The Dashboard's <Ui>Profile strength</Ui> list shows what is still missing.
      </>
    ),
  },
  {
    title: 'Save the answers every application asks for',
    body: (
      <>
        On <To to="/answers">Answers</To>, answer the questions under <Ui>Eligibility</Ui> (work authorization,
        sponsorship) and any others you want reused, and switch <Ui>Verified</Ui> on for each one you have checked.
        Only verified answers are filled for these questions.
      </>
    ),
  },
  {
    title: 'Apply',
    body: (
      <>
        Open a job on the <To to="/">job board</To> and press <Ui>Apply</Ui>. The employer's form opens in a new tab.
        Press the <Ui>JobFlo</Ui> button on that page, then <Ui>Fill application</Ui>. Read what was filled, answer what
        is left, and press the site's own submit button yourself. JobFlo records the application for you.
      </>
    ),
  },
];

interface Topic {
  id: string;
  title: string;
  body: React.ReactNode;
}

const TOPICS: Topic[] = [
  {
    id: 'jobs',
    title: 'Finding jobs',
    body: (
      <>
        <P>
          The <To to="/">job board</To> lists open roles read directly from companies' own careers pages, so every{' '}
          <Ui>Apply</Ui> button goes straight to the employer's application form. It opens on jobs posted in the past 24
          hours; change that under <Ui>Posted</Ui>.
        </P>
        <List
          items={[
            <>
              <strong>Search and filter.</strong> Search by title, company or department, and narrow by workplace, level,
              category, job type, location, salary, technologies or company. Staffing agencies are hidden unless you switch{' '}
              <Ui>Hide staffing agencies</Ui> off.
            </>,
            <>
              <strong>Ready to apply.</strong> Signed in, each job shows how many of its required questions your saved
              answers already cover. Under <Ui>Application questions</Ui>, choose <Ui>Ready to apply</Ui> to see only the
              jobs JobFlo can fill completely.
            </>,
            <>
              <strong>Save a job</strong> with the bookmark on its card or the <Ui>Save</Ui> button on its page. Press{' '}
              <Ui>Saved jobs</Ui> above the list (or in the account menu) to see them all, whenever they were posted.
              They are also under <To to="/applications">Applications</To> with the status Saved.
            </>,
            <>
              <strong>How you match</strong> (part of Pro). Each job's card shows a percentage: how many of the skills and tools the job
              names are in your resume and Experience library. Open the job and <Ui>How you match</Ui> lists which you
              have and which were <Ui>Not found</Ui>. It counts named things only; it does not judge you. A card's
              number can change once the job has been opened, because the whole posting is read then.
            </>,
            <>
              <strong>Save a search.</strong> Set your search and filters, press <Ui>Save this search</Ui> and give it a
              name. It becomes a chip above the results that brings the search back in one click.
            </>,
            <>
              <strong>Job alerts.</strong> In the same dialog, set <Ui>Email me new matches</Ui> to{' '}
              <Ui>Daily digest</Ui> or <Ui>Weekly digest</Ui>. You get an email only when there are new jobs, and every
              email has a link that stops them.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: 'fill',
    title: 'Filling an application',
    body: (
      <>
        <P>
          On an application form, a blue <Ui>JobFlo</Ui> button appears at the bottom right of the page. It opens the
          JobFlo panel in Chrome's side panel, beside the form.
        </P>
        <List
          items={[
            <>
              Choose the resume to attach under <Ui>Resume</Ui> (your default is preselected) and press{' '}
              <Ui>Fill application</Ui>.
            </>,
            <>
              The panel then lists every question in groups: <Ui>Filled</Ui>, <Ui>Needs you</Ui> (questions JobFlo leaves
              to you, with the reason), <Ui>Not in your profile</Ui> (it knows the question but you have saved nothing for
              it) and <Ui>Unrecognized fields</Ui>. Required questions that are still empty are marked.
            </>,
            <>
              Answer what is left on the form itself. Forms with several steps are filled as each step appears.
            </>,
            <>
              Review everything, then press the site's own submit button. <strong>JobFlo never submits for you.</strong>
            </>,
          ]}
        />
        <P>
          <Ui>Fill again</Ui> fills anything new without touching what is already on the form, unless you tick{' '}
          <Ui>Overwrite values already on the page</Ui>. <Ui>Refresh profile</Ui> picks up changes you just made on the
          dashboard. If you prefer the panel on the page instead of beside it, choose{' '}
          <Ui>Use the on-page panel instead</Ui> at the bottom of the side panel.
        </P>
        <P>
          <strong>Prefill on open.</strong> On the job board, set <Ui>Prefill on open</Ui> to <Ui>Prefill</Ui> and the form
          is filled as soon as you open it from the board, with no click. <Ui>Prefill with tailored resume</Ui> also builds
          and attaches a resume for that job.
        </P>
        <P>
          <strong>Other sites.</strong> JobFlo runs by itself on the application pages of the hiring systems it knows
          (Greenhouse, Lever, Ashby, Workable and others). On a careers page it does not know, click the JobFlo icon in
          Chrome's toolbar and choose <Ui>Activate on this page</Ui>.
        </P>
      </>
    ),
  },
  {
    id: 'rules',
    title: 'What JobFlo fills, and what it leaves to you',
    body: (
      <>
        <P>JobFlo is built to be safe to trust with an application. These rules have no exceptions:</P>
        <List
          items={[
            <>
              <strong>It never submits.</strong> You always press the submit button yourself.
            </>,
            <>
              <strong>It never guesses.</strong> A question it does not recognise, or is not sure about, is listed for
              you and left empty.
            </>,
            <>
              <strong>Sensitive questions need a verified answer.</strong> Work authorization, sponsorship, salary,
              background checks and the voluntary demographic questions (gender, race, veteran and disability status) are
              filled only from an answer you saved on <To to="/answers">Answers</To> and switched to <Ui>Verified</Ui>.
              Leave one blank there and JobFlo skips it every time.
            </>,
            <>
              <strong>A yes or no is not stretched.</strong> If the options say more than yes or no ("Yes, I am a
              citizen", "I hold a work visa"), or the question asks about a country other than yours, JobFlo leaves it for
              you.
            </>,
            <>
              <strong>Some things it never touches:</strong> passwords, one-time codes, payment details and government
              identifiers.
            </>,
          ]}
        />
      </>
    ),
  },
  {
    id: 'answers',
    title: 'Your saved answers',
    body: (
      <>
        <P>
          <To to="/answers">Answers</To> is where JobFlo's knowledge of you beyond your profile lives.{' '}
          <Ui>Application answers</Ui> holds the standard questions, grouped (Eligibility, Preferences, Compensation,
          Availability and so on). Enter an answer, switch <Ui>Verified</Ui> on once you have checked it, and press{' '}
          <Ui>Save</Ui>.
        </P>
        <P>
          <Ui>Custom answers</Ui> holds free-text answers to questions that are not standard ("What excites you about
          this team?"). JobFlo reuses one only when an application asks that same question.{' '}
          <Ui>Questions employers ask</Ui> shows what the forms on the job board ask most often, so you can answer the
          ones that unlock the most jobs first.
        </P>
        <P>
          <strong>Saving what you typed.</strong> After you submit an application, JobFlo offers the answers you typed
          by hand under <Ui>Save what you typed</Ui>. Tick the ones worth keeping and press <Ui>Save selected</Ui>.
          Nothing about eligibility, pay or demographics is ticked for you, and nothing is saved unless you choose it.
        </P>
      </>
    ),
  },
  {
    id: 'tailored',
    title: 'Resumes and tailored resumes',
    body: (
      <>
        <P>
          Upload the resumes you apply with on <To to="/resumes">Resumes</To>. Click the star to choose the{' '}
          <Ui>Default</Ui>; you can pick another in the panel for any one application.
        </P>
        <P>
          <strong>A tailored resume</strong> is your own resume, reordered and reworded for one job. In the panel, press{' '}
          <Ui>Fill with tailored resume</Ui>: the form is filled at once, the resume is built in the background (usually
          under a minute), and it is attached when it is ready.
        </P>
        <List
          items={[
            <>
              <strong>Nothing is invented.</strong> Every line is checked against your resume and your Experience
              library. A line that says anything they do not is put back to your own wording. Jobs, titles, dates and
              education are never changed.
            </>,
            <>
              <strong>Your Experience library.</strong> A resume has no room for everything you have done. On the Resumes
              page, <Ui>Add to library</Ui> lets you write down the rest (one thing per line, about one job or none) or
              add a document such as an old resume or a project write-up. Tailoring then draws on it too, so experience a
              job asks for can appear even when your resume leaves it out. Add only what is yours to share.
            </>,
            <>
              <strong>See what changed.</strong> <Ui>View it and what changed</Ui> in the panel, or the{' '}
              <Ui>What changed</Ui> icon on the Resumes page, shows each reworded line beside your original.{' '}
              <Ui>Preview the PDF</Ui> shows the file itself.
            </>,
            <>
              <strong>Edit it yourself.</strong> Press <Ui>Edit it</Ui> in the panel (or the <Ui>Edit</Ui> icon on the
              Resumes page) to reword, move, remove or add lines, then <Ui>Save and remake the PDF</Ui>. When you go back
              to the application, the edited resume is attached in place of the earlier one.
            </>,
            <>
              <strong>If it does not attach.</strong> Use <Ui>Attach it again</Ui>, or <Ui>Download it</Ui> and upload
              the file yourself. If a build fails, <Ui>Attach my regular resume instead</Ui> attaches your uploaded one.
            </>,
            <>
              <strong>How long they are kept.</strong> A tailored resume is removed 30 days after it was built, unless
              the application went further than a submission (an interview, an offer): those are kept.
            </>,
          ]}
        />
        <P>
          Tailored resumes are part of Pro, with a monthly allowance shown on your <To to="/plan">Plan</To> page. You can
          instead run them on your own account with a model vendor, under <Ui>Tailored resumes: model and cost</Ui> on
          the Resumes page.
        </P>
      </>
    ),
  },
  {
    id: 'tracking',
    title: 'Tracking your applications',
    body: (
      <>
        <P>
          You do not have to log anything. When you fill a form, JobFlo records the application as{' '}
          <Ui>In progress</Ui>. When the employer's site shows its confirmation page after you submit, JobFlo marks it{' '}
          <Ui>Applied</Ui> and shows <Ui>Application recorded</Ui> on the page. If a site gives no confirmation, press{' '}
          <Ui>Mark as applied</Ui> in the panel.
        </P>
        <P>
          <To to="/applications">Applications</To> shows everything, as a <Ui>List</Ui> or a <Ui>Board</Ui> with one
          column per status, from Saved through Applied, Interview scheduled and Offer received. Change a status from the
          list, or open an application to see the job description, the answers that were given (which ones JobFlo filled
          and which you typed), the resume used, your notes and a timeline.
        </P>
        <P>
          The <To to="/dashboard">Dashboard</To> sums it up: how many applications, how many in the last week, and your
          response, interview and offer rates.
        </P>
      </>
    ),
  },
  {
    id: 'email',
    title: 'Email tracking',
    body: (
      <>
        <P>
          With <To to="/email-tracking">Email tracking</To> on, JobFlo reads the emails employers send about your
          applications and keeps each status up to date: received, interview, assessment, offer or rejected. It is part
          of Pro. There are two ways to set it up:
        </P>
        <List
          items={[
            <>
              <Ui>Apply with a JobFlo address</Ui> (recommended). The extension fills your private JobFlo address into
              application forms. JobFlo reads each employer email and forwards it to your own inbox, where replying goes
              straight to the employer. Nothing to set up.
            </>,
            <>
              <Ui>Forward a copy from your own mailbox</Ui>. You keep applying with your own email, and one forwarding
              rule in Gmail or Outlook sends JobFlo a copy of mail from hiring systems. The page walks you through the
              rule and tells you when the first copy has arrived.
            </>,
          ]}
        />
        <P>
          You can switch between them or turn tracking off at any time. If JobFlo reads an email wrongly, correct it
          under <Ui>Recent mail</Ui>; your word is final.
        </P>
      </>
    ),
  },
  {
    id: 'plans',
    title: 'Plans',
    body: (
      <>
        <P>
          The job board, saved searches and alerts, filling applications and tracking them are free. Pro adds tailored
          resumes, how you match each job, and email tracking. Every new account starts with a short Pro trial. Your <To to="/plan">Plan</To>{' '}
          page shows what your plan includes and how much of it you have used.
        </P>
      </>
    ),
  },
  {
    id: 'privacy',
    title: 'Your data',
    body: (
      <>
        <P>
          The extension works only on application pages, reads only the form and the job posting there, and talks only to
          JobFlo and the job site. Your details are used to fill and track your applications and for nothing else. The{' '}
          <To to="/privacy">Privacy Policy</To> has the full account.
        </P>
        <P>
          To leave, use <Ui>Delete my account</Ui> at the bottom of <To to="/profile">Profile</To>. It removes your
          profile, answers, resumes, applications and everything else at once.
        </P>
      </>
    ),
  },
  {
    id: 'trouble',
    title: 'When something does not work',
    body: (
      <>
        <List
          items={[
            <>
              <strong>The JobFlo button is not on the page.</strong> Reload the page. If the site is one JobFlo does not
              know, click the JobFlo icon in Chrome's toolbar and choose <Ui>Activate on this page</Ui>.
            </>,
            <>
              <strong>The panel says to sign in.</strong> Open the <To to="/dashboard">Dashboard</To> and press{' '}
              <Ui>Connect extension</Ui>, or click the JobFlo toolbar icon and choose <Ui>Sign in on the dashboard</Ui>.
              The open application picks the sign-in up without a reload.
            </>,
            <>
              <strong>"JobFlo was updated. Reload this page."</strong> Chrome installed a new version while the page was
              open. Reload the application page; what you typed in the form is usually kept by the site.
            </>,
            <>
              <strong>A question was left empty.</strong> Look under <Ui>Needs you</Ui> or <Ui>Not in your profile</Ui>{' '}
              in the panel for the reason. Usually the answer is not saved yet, is not marked <Ui>Verified</Ui>, or the
              question was not clear enough to answer safely.
            </>,
            <>
              <strong>The resume was not attached.</strong> Some sites take a moment. Use <Ui>Attach it again</Ui> for a
              tailored resume, or attach the file yourself; everything else stays filled.
            </>,
            <>
              <strong>An application is missing.</strong> If the site showed no confirmation page, press{' '}
              <Ui>Mark as applied</Ui> in the panel, or <Ui>I submitted this application</Ui> from the toolbar icon.
            </>,
          ]}
        />
        <P>
          Still stuck? Write to <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </P>
      </>
    ),
  },
];

// Room for the bar that stays at the top of the window.
const UNDER_BAR = { scrollMarginTop: '88px' };

/** The user guide: a five-step start, then one section per part of the product. `/help#<section>` opens at that section. */
const GuidePage: React.FC = () => {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = 'How JobFlo works';
    // The page is drawn after the browser has looked for the address's section, so go there now.
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    return () => {
      document.title = 'JobFlo';
    };
  }, [hash]);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '220px minmax(0, 1fr)' }, gap: { xs: 2, md: 6 } }}>
        <Box component="nav" aria-label="On this page" sx={{ display: { xs: 'none', md: 'block' } }}>
          <Stack spacing={1} sx={{ position: 'sticky', top: 96 }}>
            <Typography variant="overline" color="text.secondary">
              On this page
            </Typography>
            <Link href="#start" underline="hover" variant="body2">
              Start here
            </Link>
            {TOPICS.map((topic) => (
              <Link key={topic.id} href={`#${topic.id}`} underline="hover" variant="body2">
                {topic.title}
              </Link>
            ))}
          </Stack>
        </Box>

        <Box sx={{ maxWidth: 760 }}>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            How JobFlo works
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ fontWeight: 400, mb: 3 }}>
            {TAGLINE} JobFlo finds jobs on companies' own careers pages, fills the application from your profile, and keeps
            track of every one. You review each form and submit it yourself.
          </Typography>

          <Box component="section" id="start" aria-labelledby="start-title" sx={{ mb: 5, ...UNDER_BAR }}>
            <Typography id="start-title" variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
              Start here: your first application in five steps
            </Typography>
            <Stack component="ol" spacing={1.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {STEPS.map((step, index) => (
                <Paper key={step.title} component="li" variant="outlined" sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    aria-hidden="true"
                    sx={{ flex: 'none', width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
                  >
                    {index + 1}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body1">{step.body}</Typography>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </Box>

          {TOPICS.map((topic) => (
            <Box key={topic.id} component="section" id={topic.id} aria-labelledby={`${topic.id}-title`} sx={{ mb: 4, ...UNDER_BAR }}>
              <Typography id={`${topic.id}-title`} variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>
                {topic.title}
              </Typography>
              {topic.body}
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default GuidePage;
